/**
 * workInstructions.js - Dedicated work instructions generator for OptimalAvailability Studio
 * Generates formatted work instructions from RCM analysis, maintenance plans, and templates
 * Matching AWB 4.0's WorkInstructionTemplate.docx capability
 *
 * @module engine/workInstructions
 * @exports WorkInstructionGenerator
 */

class WorkInstructionGenerator {
  /**
   * Optimal branding colors
   * @static
   */
  static BRAND_COLORS = {
    NAVY: '#1B2A4A',
    GOLD: '#E8A838',
    BLUE: '#2D5F8A',
    LIGHT_GRAY: '#F5F5F5',
    TEXT_DARK: '#333333',
  };

  /**
   * Work instruction template structures
   * @static
   */
  static TEMPLATES = {
    PREVENTIVE: {
      name: 'Preventive Maintenance',
      category: 'preventive',
      description: 'Scheduled maintenance to prevent failures',
      sections: [
        'header',
        'scope',
        'frequency',
        'tools_materials',
        'safety',
        'procedure',
        'quality_checks',
        'documentation',
      ],
    },
    CORRECTIVE: {
      name: 'Corrective Maintenance',
      category: 'corrective',
      description: 'Repair or replacement following a failure',
      sections: [
        'header',
        'failure_description',
        'tools_materials',
        'safety',
        'procedure',
        'verification',
        'quality_checks',
        'restart_procedure',
        'documentation',
      ],
    },
    INSPECTION: {
      name: 'Inspection & Condition Monitoring',
      category: 'inspection',
      description: 'Periodic inspection or monitoring task',
      sections: [
        'header',
        'inspection_objectives',
        'tools_materials',
        'safety',
        'procedure',
        'acceptance_criteria',
        'trending',
        'documentation',
      ],
    },
    OVERHAUL: {
      name: 'Equipment Overhaul',
      category: 'overhaul',
      description: 'Major rebuild or comprehensive maintenance',
      sections: [
        'header',
        'scope',
        'tools_materials',
        'safety',
        'disassembly',
        'inspection_repair',
        'reassembly',
        'testing',
        'documentation',
      ],
    },
  };

  /**
   * Generate a structured work instruction from parameters
   *
   * @param {Object} params - Work instruction parameters
   * @param {Object} params.task - Task details
   * @param {string} params.task.id - Task ID
   * @param {string} params.task.name - Task name
   * @param {string} params.task.description - Task description
   * @param {string} params.task.type - Task type (preventive, corrective, inspection, overhaul)
   * @param {Array<Object>} params.task.steps - Procedure steps with { title, description, duration, equipment, resources }
   * @param {Array<string>} params.task.safetyNotes - Safety precautions
   * @param {Object} params.equipment - Equipment being serviced
   * @param {string} params.equipment.id - Equipment ID
   * @param {string} params.equipment.name - Equipment name
   * @param {string} params.equipment.location - Equipment location
   * @param {string} params.equipment.model - Equipment model
   * @param {string} params.equipment.serialNumber - Serial number
   * @param {Array<string>} params.toolsRequired - Tools needed
   * @param {Array<string>} params.materialsRequired - Materials/parts needed
   * @param {Array<string>} params.sparesRequired - Spare parts required
   * @param {Array<Object>} params.qualityChecks - Quality verification checks
   * @param {string} params.estimatedDuration - Duration estimate (e.g., "2 hours")
   * @param {string} params.skillLevel - Required skill level (Basic, Intermediate, Advanced, Expert)
   * @param {string} params.frequency - Maintenance frequency (for preventive tasks)
   * @returns {Object} Structured work instruction object
   */
  static generateInstruction(params) {
    const {
      task = {},
      equipment = {},
      toolsRequired = [],
      materialsRequired = [],
      sparesRequired = [],
      qualityChecks = [],
      estimatedDuration = 'TBD',
      skillLevel = 'Intermediate',
      frequency = 'N/A',
    } = params;

    const instruction = {
      id: task.id || `WI-${Date.now()}`,
      name: task.name || 'Untitled Work Instruction',
      description: task.description || '',
      type: task.type || 'preventive',
      createdDate: new Date().toISOString(),
      revision: 1,

      equipment: {
        id: equipment.id || '',
        name: equipment.name || '',
        location: equipment.location || '',
        model: equipment.model || '',
        serialNumber: equipment.serialNumber || '',
      },

      scope: {
        objective: task.objective || 'See procedure below',
        scope: task.scope || '',
        frequency,
        estimatedDuration,
        skillLevelRequired: skillLevel,
      },

      requirements: {
        tools: toolsRequired,
        materials: materialsRequired,
        spareParts: sparesRequired,
      },

      safety: {
        hazards: task.safetyWarnings || [],
        precautions: task.safetyPrecautions || [],
        ppe: task.ppe || [],
        lockoutRequired: task.lockoutRequired || false,
      },

      procedure: {
        steps: task.steps || [],
        totalSteps: (task.steps || []).length,
      },

      qualityControl: {
        checks: qualityChecks,
        acceptanceCriteria: task.acceptanceCriteria || '',
      },

      documentation: {
        recordsRequired: task.recordsRequired || ['Completion Date', 'Technician Name', 'Notes'],
        attachments: [],
      },

      approvalWorkflow: {
        preparedBy: '',
        approvedBy: '',
        effectiveDate: new Date().toISOString(),
      },
    };

    return instruction;
  }

  /**
   * Auto-generate work instruction from RCM analysis output
   * The task recommendation from RCM feeds directly into a work instruction
   *
   * @param {Object} rcmCause - RCM cause/recommendation
   * @param {string} rcmCause.taskId - Task ID
   * @param {string} rcmCause.description - Task description
   * @param {string} rcmCause.failureMode - Failure mode being addressed
   * @param {string} rcmCause.interval - Recommended interval
   * @param {string} rcmCause.taskType - Task type (on-condition, scheduled, none)
   * @param {Object} equipment - Equipment data
   * @param {Object} projectData - Project data
   * @returns {Object} Generated work instruction
   */
  static generateFromRCM(rcmCause, equipment, projectData = {}) {
    const taskType = this._mapRCMTaskType(rcmCause.taskType);

    const instruction = this.generateInstruction({
      task: {
        id: rcmCause.taskId || `WI-RCM-${Date.now()}`,
        name: `${rcmCause.taskType} Task: ${rcmCause.description}`,
        description: `Maintenance task recommended by RCM analysis.\n\nFailure Mode: ${rcmCause.failureMode}\nRecommended Interval: ${rcmCause.interval}`,
        type: taskType,
        objective: `Prevent failure mode: ${rcmCause.failureMode}`,
        steps: this._generateStepsFromRCM(rcmCause, equipment),
        safetyWarnings: rcmCause.safetyWarnings || [],
        safetyPrecautions: rcmCause.safetyPrecautions || [],
        acceptanceCriteria: rcmCause.acceptanceCriteria || 'Equipment operates within normal parameters',
      },
      equipment,
      toolsRequired: rcmCause.toolsRequired || [],
      materialsRequired: rcmCause.materialsRequired || [],
      sparesRequired: rcmCause.spareParts || [],
      qualityChecks: rcmCause.qualityChecks || [],
      estimatedDuration: rcmCause.estimatedHours ? `${rcmCause.estimatedHours} hours` : 'TBD',
      skillLevel: rcmCause.skillLevel || 'Intermediate',
      frequency: rcmCause.interval || 'N/A',
    });

    // Mark as RCM-derived
    instruction.source = 'RCM Analysis';
    instruction.rcmReference = rcmCause.taskId;

    return instruction;
  }

  /**
   * Generate work instruction from a scheduled maintenance task
   *
   * @param {Object} scheduledTask - Scheduled task from maintenance plan
   * @param {Object} equipment - Equipment data
   * @param {Array<Object>} spares - Available spare parts
   * @param {Array<string>} labor - Labor resources available
   * @returns {Object} Generated work instruction
   */
  static generateFromMaintenancePlan(scheduledTask, equipment, spares = [], labor = []) {
    const taskSteps = this._generateStepsFromScheduledTask(scheduledTask, spares);

    const instruction = this.generateInstruction({
      task: {
        id: scheduledTask.id || `WI-PM-${Date.now()}`,
        name: scheduledTask.description,
        description: `Scheduled maintenance task from maintenance plan.\n\nInterval: ${scheduledTask.interval} hours`,
        type: 'preventive',
        objective: `Perform ${scheduledTask.description}`,
        steps: taskSteps,
        scope: scheduledTask.scope || '',
        safetyWarnings: scheduledTask.safetyWarnings || [],
        safetyPrecautions: scheduledTask.safetyPrecautions || [],
        estimatedHours: scheduledTask.estimatedHours || null,
      },
      equipment,
      toolsRequired: scheduledTask.resources || [],
      materialsRequired: scheduledTask.materials || [],
      sparesRequired: this._mapSparePartsFromScheduledTask(scheduledTask, spares),
      qualityChecks: scheduledTask.qualityChecks || [],
      estimatedDuration: scheduledTask.estimatedHours
        ? `${scheduledTask.estimatedHours} hours`
        : 'TBD',
      skillLevel: scheduledTask.skillLevel || 'Intermediate',
      frequency: scheduledTask.interval || 'N/A',
    });

    instruction.source = 'Maintenance Plan';
    instruction.maintenanceInterval = scheduledTask.interval;

    return instruction;
  }

  /**
   * Generate batch work instructions for multiple tasks
   * Efficiently creates instructions for entire maintenance plan
   *
   * @param {Array<Object>} tasks - Tasks (RCM or scheduled)
   * @param {Object} projectData - Project data including equipment list
   * @param {Object} options - Generation options
   * @param {boolean} options.includeScheduled - Include scheduled tasks
   * @param {boolean} options.includeCorrectiveTemplates - Include corrective task templates
   * @returns {Array<Object>} Generated work instructions
   */
  static generateBatchInstructions(tasks, projectData, options = {}) {
    const opts = {
      includeScheduled: options.includeScheduled !== false,
      includeCorrectiveTemplates: options.includeCorrectiveTemplates !== false,
    };

    const instructions = [];

    tasks.forEach(task => {
      // Locate equipment for this task
      const equipment = (projectData.equipment || []).find(
        eq => eq.id === task.equipmentId || eq.name === task.equipment
      ) || { name: task.equipment || 'Unknown Equipment' };

      if (task.taskType === 'scheduled' || task.interval) {
        // This is a scheduled task
        const instruction = this.generateFromScheduledTask(task, equipment);
        instructions.push(instruction);
      } else if (task.failureMode) {
        // This is an RCM-derived task
        const instruction = this.generateFromRCM(task, equipment, projectData);
        instructions.push(instruction);
      }
    });

    return instructions;
  }

  /**
   * Format work instruction as printable HTML
   * Matches Optimal branding and styling
   *
   * @param {Object} instruction - Work instruction object
   * @param {Object} options - Formatting options
   * @param {boolean} options.includeSignatures - Include signature blocks
   * @param {boolean} options.forPrint - Optimize for printing
   * @returns {string} HTML document
   */
  static formatAsHTML(instruction, options = {}) {
    const opts = {
      includeSignatures: options.includeSignatures !== false,
      forPrint: options.forPrint !== false,
    };

    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    let html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${instruction.name}</title>
        <style>
          ${this._getPageStyles(opts.forPrint)}
        </style>
      </head>
      <body>
        <div class="header-section">
          <h1>${instruction.name}</h1>
          <div class="metadata">
            <div>
              <span class="label">Work Instruction ID:</span> ${instruction.id}
            </div>
            <div>
              <span class="label">Equipment:</span> ${instruction.equipment.name}
              <span style="margin-left: 30px; color: #666;">Location: ${instruction.equipment.location || 'N/A'}</span>
            </div>
            <div>
              <span class="label">Type:</span> ${this._formatTaskType(instruction.type)}
              <span style="margin-left: 30px;">
                <span class="label">Revision:</span> ${instruction.revision}
              </span>
              <span style="margin-left: 30px;">
                <span class="label">Date:</span> ${dateStr}
              </span>
            </div>
          </div>
        </div>

        ${this._formatScope(instruction)}
        ${this._formatRequirements(instruction)}
        ${this._formatSafety(instruction)}
        ${this._formatProcedure(instruction)}
        ${this._formatQualityControl(instruction)}
        ${opts.includeSignatures ? this._formatSignatures(instruction) : ''}
      </body>
      </html>
    `;

    return html;
  }

  /**
   * Get available templates
   *
   * @returns {Array<Object>} Template array
   */
  static getTemplates() {
    return Object.values(this.TEMPLATES).map(template => ({
      id: template.name.toLowerCase().replace(/\s+/g, '-'),
      ...template,
    }));
  }

  /**
   * Private helper: Map RCM task type to template category
   * @private
   */
  static _mapRCMTaskType(rcmTaskType) {
    const mapping = {
      'on-condition': 'inspection',
      'condition-monitoring': 'inspection',
      'scheduled': 'preventive',
      'no-scheduled-task': 'corrective',
      'redesign': 'overhaul',
    };

    return mapping[rcmTaskType?.toLowerCase()] || 'preventive';
  }

  /**
   * Private helper: Generate procedure steps from RCM cause
   * @private
   */
  static _generateStepsFromRCM(rcmCause, equipment) {
    const steps = [];

    // Generic steps based on task type
    if (rcmCause.taskType === 'on-condition' || rcmCause.taskType === 'condition-monitoring') {
      steps.push(
        {
          title: 'Preparation',
          description: `Prepare equipment for inspection. Ensure ${equipment.name} is accessible and properly positioned.`,
          duration: '0.25 hours',
        },
        {
          title: 'Condition Assessment',
          description: `Inspect equipment for condition indicators. Look for: ${rcmCause.conditionIndicators?.join(', ') || 'visible wear, noise, vibration, temperature anomalies'}`,
          duration: '0.5 hours',
        },
        {
          title: 'Documentation',
          description: 'Record all observations and measurements. Compare to baseline values.',
          duration: '0.25 hours',
        }
      );
    } else {
      steps.push(
        {
          title: 'Preparation',
          description: `Prepare equipment and workspace. Gather all required tools and materials.`,
          duration: '0.5 hours',
        },
        {
          title: 'Execution',
          description: rcmCause.description || 'Perform the maintenance task according to manufacturer specifications.',
          duration: rcmCause.estimatedHours ? `${rcmCause.estimatedHours} hours` : '1 hour',
        },
        {
          title: 'Verification',
          description: 'Verify that the maintenance task was completed successfully.',
          duration: '0.5 hours',
        }
      );
    }

    return steps;
  }

  /**
   * Private helper: Generate steps from scheduled task
   * @private
   */
  static _generateStepsFromScheduledTask(task, spares = []) {
    const steps = task.procedure || [];

    if (steps.length === 0) {
      // Generate generic steps
      return [
        {
          title: 'Preparation',
          description: 'Prepare equipment and workspace. Gather all tools and materials.',
          duration: '0.5 hours',
        },
        {
          title: 'Perform Maintenance',
          description: task.description || 'Execute maintenance task per manufacturer specifications.',
          duration: task.estimatedHours ? `${task.estimatedHours} hours` : '1 hour',
        },
        {
          title: 'Verification',
          description: 'Verify task completion and document results.',
          duration: '0.5 hours',
        },
      ];
    }

    return steps;
  }

  /**
   * Private helper: Map spare parts from scheduled task to instruction format
   * @private
   */
  static _mapSparePartsFromScheduledTask(task, spares = []) {
    if (!task.sparePartIds) return [];

    return task.sparePartIds
      .map(id => spares.find(s => s.id === id))
      .filter(Boolean)
      .map(part => `${part.name} (Qty: ${part.quantity || 1})`)
      .filter(Boolean);
  }

  /**
   * Private helper: Format scope section
   * @private
   */
  static _formatScope(instruction) {
    return `
      <div class="section">
        <h2>Scope & Objectives</h2>
        <div class="metadata">
          <div>
            <span class="label">Task Type:</span> ${this._formatTaskType(instruction.type)}
          </div>
          <div>
            <span class="label">Frequency:</span> ${instruction.scope.frequency}
          </div>
          <div>
            <span class="label">Estimated Duration:</span> ${instruction.scope.estimatedDuration}
          </div>
          <div>
            <span class="label">Skill Level Required:</span> ${instruction.scope.skillLevelRequired}
          </div>
        </div>
        <h3>Objective</h3>
        <p>${instruction.scope.objective}</p>
        ${instruction.scope.scope ? `<h3>Scope</h3><p>${instruction.scope.scope}</p>` : ''}
      </div>
    `;
  }

  /**
   * Private helper: Format requirements section
   * @private
   */
  static _formatRequirements(instruction) {
    const { tools, materials, spareParts } = instruction.requirements;

    let html = '<div class="section"><h2>Requirements</h2>';

    if (tools.length > 0) {
      html += '<h3>Tools Required</h3><ul>';
      tools.forEach(tool => {
        html += `<li>${tool}</li>`;
      });
      html += '</ul>';
    }

    if (materials.length > 0) {
      html += '<h3>Materials Required</h3><ul>';
      materials.forEach(material => {
        html += `<li>${material}</li>`;
      });
      html += '</ul>';
    }

    if (spareParts.length > 0) {
      html += '<h3>Spare Parts Required</h3><ul>';
      spareParts.forEach(part => {
        html += `<li>${part}</li>`;
      });
      html += '</ul>';
    }

    html += '</div>';
    return html;
  }

  /**
   * Private helper: Format safety section
   * @private
   */
  static _formatSafety(instruction) {
    const { hazards, precautions, ppe, lockoutRequired } = instruction.safety;

    let html = '<div class="section"><h2>Safety</h2>';

    if (lockoutRequired) {
      html += `
        <div class="safety-warning">
          ⚠ <strong>LOCKOUT REQUIRED</strong><br>
          This task requires equipment lockout/tagout (LOTO) procedures. Ensure all energy sources are isolated before beginning work.
        </div>
      `;
    }

    if (hazards.length > 0) {
      html += '<h3>Hazards</h3><ul>';
      hazards.forEach(hazard => {
        html += `<li>${hazard}</li>`;
      });
      html += '</ul>';
    }

    if (precautions.length > 0) {
      html += '<h3>Precautions</h3><ul>';
      precautions.forEach(precaution => {
        html += `<li>${precaution}</li>`;
      });
      html += '</ul>';
    }

    if (ppe.length > 0) {
      html += '<h3>Personal Protective Equipment (PPE)</h3><ul>';
      ppe.forEach(item => {
        html += `<li>${item}</li>`;
      });
      html += '</ul>';
    }

    html += '</div>';
    return html;
  }

  /**
   * Private helper: Format procedure section
   * @private
   */
  static _formatProcedure(instruction) {
    let html = '<div class="section"><h2>Procedure</h2>';

    if (instruction.procedure.steps.length === 0) {
      html += '<p>See task description above.</p>';
    } else {
      instruction.procedure.steps.forEach((step, idx) => {
        html += `
          <div class="step">
            <span class="step-number">${idx + 1}</span>
            <strong>${step.title || `Step ${idx + 1}`}</strong>
            ${step.duration ? `<span style="float: right; color: #666;">${step.duration}</span>` : ''}
            <p style="clear: both;">${step.description || ''}</p>
          </div>
        `;
      });
    }

    html += '</div>';
    return html;
  }

  /**
   * Private helper: Format quality control section
   * @private
   */
  static _formatQualityControl(instruction) {
    const { checks, acceptanceCriteria } = instruction.qualityControl;

    let html = '<div class="section"><h2>Quality Control & Verification</h2>';

    if (acceptanceCriteria) {
      html += `<h3>Acceptance Criteria</h3><p>${acceptanceCriteria}</p>`;
    }

    if (checks.length > 0) {
      html += `
        <h3>Quality Checks</h3>
        <table>
          <thead>
            <tr>
              <th>Check</th>
              <th>Criteria</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
      `;

      checks.forEach(check => {
        html += `
          <tr>
            <td>${check.description || ''}</td>
            <td>${check.criteria || ''}</td>
            <td style="text-align: center;">☐ Pass  ☐ Fail</td>
          </tr>
        `;
      });

      html += `
          </tbody>
        </table>
      `;
    }

    html += '</div>';
    return html;
  }

  /**
   * Private helper: Format signatures section
   * @private
   */
  static _formatSignatures(instruction) {
    return `
      <div class="section">
        <h2>Sign-Off & Documentation</h2>
        <div style="margin-top: 30px;">
          <div style="display: inline-block; margin-right: 50px;">
            <div style="border-bottom: 1px solid #000; width: 250px;"></div>
            <div style="font-size: 10pt; margin-top: 5px;">Technician Name & Signature</div>
            <div style="border-bottom: 1px solid #000; width: 250px; margin-top: 10px;"></div>
            <div style="font-size: 10pt; margin-top: 5px;">Date</div>
          </div>
          <div style="display: inline-block;">
            <div style="border-bottom: 1px solid #000; width: 250px;"></div>
            <div style="font-size: 10pt; margin-top: 5px;">Supervisor/Inspector Name & Signature</div>
            <div style="border-bottom: 1px solid #000; width: 250px; margin-top: 10px;"></div>
            <div style="font-size: 10pt; margin-top: 5px;">Date</div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Private helper: Format task type for display
   * @private
   */
  static _formatTaskType(type) {
    const map = {
      preventive: 'Preventive Maintenance',
      corrective: 'Corrective Maintenance',
      inspection: 'Inspection/Monitoring',
      overhaul: 'Equipment Overhaul',
    };

    return map[type] || type;
  }

  /**
   * Private helper: Get page styles for HTML export
   * @private
   */
  static _getPageStyles(forPrint = true) {
    return `
      @media print {
        body { margin: 0; padding: 0; }
        .page-break { page-break-after: always; }
        .no-break { page-break-inside: avoid; }
      }

      html, body {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        font-size: 11pt;
        color: ${WorkInstructionGenerator.BRAND_COLORS.TEXT_DARK};
        line-height: 1.5;
        margin: 0;
        padding: 20px;
        background: white;
      }

      h1 {
        color: ${WorkInstructionGenerator.BRAND_COLORS.NAVY};
        font-size: 24pt;
        margin-top: 20px;
        margin-bottom: 10px;
        border-bottom: 3px solid ${WorkInstructionGenerator.BRAND_COLORS.GOLD};
        padding-bottom: 10px;
      }

      h2 {
        color: ${WorkInstructionGenerator.BRAND_COLORS.BLUE};
        font-size: 16pt;
        margin-top: 16px;
        margin-bottom: 8px;
      }

      h3 {
        color: ${WorkInstructionGenerator.BRAND_COLORS.BLUE};
        font-size: 13pt;
        margin-top: 12px;
        margin-bottom: 6px;
      }

      .header-section {
        text-align: left;
        margin-bottom: 20px;
        border-top: 3px solid ${WorkInstructionGenerator.BRAND_COLORS.GOLD};
        border-bottom: 3px solid ${WorkInstructionGenerator.BRAND_COLORS.GOLD};
        padding: 15px 0;
      }

      .section {
        margin-bottom: 20px;
        padding-bottom: 15px;
        page-break-inside: avoid;
      }

      .metadata {
        margin: 10px 0;
        font-size: 11pt;
      }

      .metadata div {
        margin: 5px 0;
      }

      .label {
        font-weight: bold;
        color: ${WorkInstructionGenerator.BRAND_COLORS.BLUE};
        display: inline-block;
        min-width: 150px;
      }

      .step {
        margin: 12px 0;
        padding: 10px;
        border-left: 3px solid ${WorkInstructionGenerator.BRAND_COLORS.BLUE};
        background-color: ${WorkInstructionGenerator.BRAND_COLORS.LIGHT_GRAY};
        page-break-inside: avoid;
      }

      .step-number {
        display: inline-block;
        background-color: ${WorkInstructionGenerator.BRAND_COLORS.NAVY};
        color: white;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        text-align: center;
        line-height: 28px;
        margin-right: 10px;
        font-weight: bold;
      }

      .safety-warning {
        background-color: #FFF3CD;
        border-left: 4px solid #FF6B6B;
        padding: 12px;
        margin: 12px 0;
        font-weight: bold;
        color: #856404;
        page-break-inside: avoid;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin: 12px 0;
        page-break-inside: avoid;
      }

      th {
        background-color: ${WorkInstructionGenerator.BRAND_COLORS.NAVY};
        color: white;
        padding: 10px;
        text-align: left;
        font-weight: bold;
        border: 1px solid ${WorkInstructionGenerator.BRAND_COLORS.BLUE};
      }

      td {
        padding: 8px 10px;
        border: 1px solid #CCCCCC;
      }

      tr:nth-child(even) {
        background-color: ${WorkInstructionGenerator.BRAND_COLORS.LIGHT_GRAY};
      }

      ul {
        margin: 8px 0;
        padding-left: 25px;
      }

      li {
        margin: 4px 0;
      }

      p {
        margin: 10px 0;
      }
    `;
  }
}

export default WorkInstructionGenerator;
